import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextareaAutosize,
  FormControlLabel,
  Divider,
  FormControl,
  Radio,
  RadioGroup,
  IconButton,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReplayCircleFilledIcon from "@mui/icons-material/ReplayCircleFilled";
import {
  Link,
  Place,
  Edit,
  NotificationsNone,
  Close,
} from "@mui/icons-material";

import { Controller, useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import moment from "moment";

import {
  CollapsibleInfo,
  DropdownV1,
  ButtonV1,
  ModalBox,
  Loader,
  FloatingBar,
  TextField,
  DatePickerV1,
  UploadPdf,
  TooltipLabel,
} from "components";
import {
  ShipmentHeader,
  GridSkeleton,
  ShipmentDeliveryData,
  CustomViewOnly,
  CustomTextField,
  ErrorMsg,
} from "./ShipmentCustomComponents";

import ShipmentLineItem from "./ShipmentLineItem";
import EditTrackingDetailsModal from "./EditTrackingDetailsModal";
import ZohoOtherServiceCharges from "./ZohoOtherServiceCharges";
import FileUpload from "./FileUpload";

import { getOtherServicesModel, makeShipmentUpdationData } from "./DataModel";
import {
  getTotalInvoiceAmt_baseValue,
  getTotalInvoiceAmount_withGST,
  registerServiceChargeValues,
  moneyString,
  checkInvoiceValues,
  getZohoTotalInvoiceAmt_otherServices,
} from "./shipmentCalculationUtil";
import {
  hookFormLabel,
  INVOICED_VALIDATION_MSG,
  INVOICED_FILE_VALIDATION_MSG,
  success,
  error,
  shipmentStatus,
  SHIPMENT_STATUS,
  SHIPMENT_STATUS_CHIP,
  fullHold,
  partialHold,
  release,
  OTHER_REASON_VALUE,
} from "./constants";
import { ShipmentUpdateSchema } from "./TestSchema";
import theme from "themeProvider";
import {
  moneyFormat,
  getStartDate,
  dateConvertor,
  decimalRoundOff,
  downloadPdf,
} from "utils";
import { getCall_v2, postCall_v2, putCall_v2 } from "services/updateCall";
import { getShipFromAddress, getTcSyncStatusColor } from "../makeData";
import { getZohoOtherServicesModel } from "./DataModel/otherServicesModel";
import {
  getInvoicePostingStatus,
  shouldDisableTrackingField,
} from "./DataModel/shipmentUpdate";
import {
  useToaster,
  useCategoryTypeCheck,
  useDownloadInvoiceFile,
} from "hooks";

const HoldPayoutTextField = ({ control, holdType }) => {
  return (
    <Controller
      control={control}
      name="payoutHoldAmount"
      render={({ field: { value, onChange } }) => {
        return (
          <TextField
            customPadding={10}
            value={value}
            onChange={onChange}
            type="number"
            disabled={holdType === fullHold}
            placeholder="Enter the hold amount"
          />
        );
      }}
    />
  );
};

const ShipmentUpdate = ({
  shipmentInfo,
  id,
  getShipmentData,
  lineItems,
  zohoProcessingCharges,
  tcSyncData,
}) => {
  const triggerToaster = useToaster();
  const { isDirect } = useCategoryTypeCheck();
  const { triggerDownloadInvoice } = useDownloadInvoiceFile();

  const [shipmentDetails, setShipmentDetails] = useState(null);
  const [isOtherServicesModalOpen, setIsOtherServicesModalOpen] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [creditPeriod, setCreditPeriod] = useState(0);
  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);
  const [deliveryProofFile, setDeliveryProofFile] = useState(null);
  const [podDoc, setPodDoc] = useState(null);

  const [editTrackingDetailsModalOpen, setEditTrackingDetailsModalOpen] =
    useState(false);
  const [trackingUpdateLoading, setTrackingUpdateLoading] = useState(false);
  const [trackingModalData, setTrackingModalData] = useState({
    truckNumber: "",
    driverNumber: "",
    driverName: "",
    expectedReportingTime: "",
    trackingReason: "",
    trackingOtherReason: "",
  });

  const shipmentList = useSelector((state) => state.shipmentDetails);
  const orderDetails = useSelector((state) => state.orderDetails);

  const fileUploadRef = useRef(null);

  const isCreditOrder = useMemo(
    () => orderDetails.orderPaymentType === "Credit",
    [orderDetails],
  );

  const methods = useForm({
    mode: "onTouched",
    resolver: yupResolver(
      ShipmentUpdateSchema(orderDetails, shipmentDetails?.lineItemsDetails),
    ),
    defaultValues: {
      lineItemsDetails: [],
      zohoServiceCharges: [],
    },
  });
  const {
    handleSubmit,
    getValues,
    control,
    setValue,
    reset,
    clearErrors,
    formState: { errors, isDirty },
    watch,
    resetField,
    setError,
  } = methods;

  const holdType = watch("holdType");
  const invoiceDueDate = watch("invoiceDueDate");
  const actualDeliveryDate = watch("actualDeliveryDate");
  const otherChargeRef = useRef(null);

  /* const isValidShortClose = (shipmentRes) => {
    const { currentState } = shipmentRes;
    return (
      ((isDistributed || isPrivateBrand) &&
        currentState.state === SHIPMENT_STATUS.INVOICED) ||
      (isDirect && currentState.state === SHIPMENT_STATUS.DISPATCHED)
    );
  };

  const checkShortClose = (dispatchedCrossedThreshold) => {
    if (dispatchedCrossedThreshold) {
      setIsConfirmModalOpen(true);
    }
  };

  const handleConfirm = async () => {
    setIsPayoutModal(true);
  };

  const handleShortClose = async (isPayout) => {
    try {
      setIsShortCloseLoader(true);
      const res = await postCall_v2(
        `oms/order/short-close`,
        {},
        {
          customerOrdNum: orderDetails?.orderNumber,
          isPayoutApplicable: isPayout,
          isManualShortClose: true,
        },
      );
      if (res) {
        dispatch(updateOrderDetailsAction(orderDetails?.orderNumber));
        triggerToaster(`SKU's short closed`, success);
      }
    } catch (err) {
      triggerToaster("Something went wrong. Try after sometime", error);
      console.log("error", err);
    } finally {
      setIsConfirmModalOpen(false);
      setIsPayoutModal(false);
      setIsShortCloseLoader(false);
    }
  };
*/
  const currentTcSync = tcSyncData?.find(
    (item) => item.shipmentNumber === shipmentDetails?.number,
  );

  const handleRetryTcSync = async (invoiceId) => {
    try {
      const res = await postCall_v2(
        `/joms/api/internal/trigger-tc-update-to-jodl?invoiceNumber=${invoiceId}`,
      );
      triggerToaster("Successfully Triggered", success);
    } catch {
      triggerToaster(
        "Error while fetching supply source details, Please resolve and retry",
        error,
      );
    }
  };

  const onSubmitShipment = async () => {
    setLoading(true);
    const result = makeShipmentUpdationData(
      getValues(),
      shipmentDetails,
      shipmentList,
      isCreditOrder,
      isDirect,
    );

    const {
      currentState: { state },
    } = shipmentDetails;
    if (getValues("shipmentStatus") === SHIPMENT_STATUS?.READY_TO_SHIP) {
      const checkInvoiceData = checkInvoiceValues(getValues());
      if (!checkInvoiceData) {
        triggerToaster(INVOICED_VALIDATION_MSG, error);
        setLoading(false);
        return;
      }
      const files =
        fileUploadRef?.current?.getUploadedInvoices() &&
        Object.keys(fileUploadRef?.current?.getUploadedInvoices());
      if (!files?.length) {
        triggerToaster(INVOICED_FILE_VALIDATION_MSG, error);
        setLoading(false);
        return;
      }
    }
    await updateShipment(result, shipmentDetails?.number);
  };

  const updateShipment = async (result, shipmentNumber) => {
    try {
      const data = await putCall_v2("/oms/shipment", result);
      if (data) {
        getShipmentData();
        triggerToaster(`Shipment updated successfully`, success);
      }
    } catch (err) {
      if (err?.data?.detail) {
        triggerToaster(err?.data?.detail, error);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleOtherServicesModal = () =>
    setIsOtherServicesModalOpen(!isOtherServicesModalOpen);

  const resetUnconfirmedCharges = () => {
    otherChargeRef.current.resetValues();
    toggleOtherServicesModal();
    retriggerCalculation();
  };

  const calculateInvoiceAmt = useCallback(() => {
    const {
      // taxprocessingCharge,
      // taxloadingCharge,
      // taxfreightCharge,
      // taxotherCharge,
      totalInvoiceOtherServicesAmt,
    } = hookFormLabel;
    let invoiceAmtOtherServices = 0;
    invoiceAmtOtherServices = getZohoTotalInvoiceAmt_otherServices(
      getValues("zohoServiceCharges"),
    );

    setValue(
      totalInvoiceOtherServicesAmt,
      decimalRoundOff(invoiceAmtOtherServices),
    );

    retriggerCalculation();
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [
    getValues(hookFormLabel.confirmprocessingCharge),
    getValues(hookFormLabel.gstprocessingCharge),
    getValues(hookFormLabel.confirmloadingCharge),
    getValues(hookFormLabel.gstloadingCharge),
    getValues(hookFormLabel.confirmfreightCharge),
    getValues(hookFormLabel.gstfreightCharge),
    getValues(hookFormLabel.confirmotherCharge),
    getValues(hookFormLabel.gstotherCharge),
  ]);

  const retriggerCalculation = () => {
    const sumOfOtherChargesWithoutTax =
      Number(getValues(hookFormLabel.confirmprocessingCharge) ?? 0) +
      Number(getValues(hookFormLabel.confirmfreightCharge) ?? 0) +
      Number(getValues(hookFormLabel.confirmloadingCharge) ?? 0) +
      Number(getValues(hookFormLabel.confirmotherCharge) ?? 0);
    const totalInvBaseAmount = getTotalInvoiceAmt_baseValue(
      getValues(),
      shipmentDetails?.lineItemsDetails,
      sumOfOtherChargesWithoutTax,
    );
    const totalGSTInvAmount = getTotalInvoiceAmount_withGST(
      getValues(),
      shipmentDetails,
      sumOfOtherChargesWithoutTax,
      orderDetails,
    );
    let totalInvAmount =
      Number(totalInvBaseAmount ?? 0) + Number(totalGSTInvAmount ?? 0);
    totalInvAmount = decimalRoundOff(totalInvAmount);
    setValue(hookFormLabel.totalBaseInvoiceAmt, totalInvBaseAmount);
    setValue(hookFormLabel.totalGSTInvoiceAmount, totalGSTInvAmount);
    setValue(hookFormLabel.totalInvoiceAmt, totalInvAmount);
  };

  const setOtherServiceCharges = (invoiceOtherAmounts = {}) => {
    return registerServiceChargeValues(setValue, invoiceOtherAmounts);
  };

  const getCreditPeriod = useCallback(async () => {
    try {
      const res = await getCall_v2(
        `oms/credit/getCreditDueDayByShipmentNumber/${shipmentInfo.number}`,
      );
      setCreditPeriod(res.data);
    } catch (error) {
      console.warn("Unable to fetch the credit period", error);
    }
  }, [shipmentInfo]);

  useEffect(() => {
    setShipmentDetails(shipmentInfo);

    const { invoiceOtherAmounts } = shipmentInfo?.shipmentInvoiceDetails; //NOSONAR
    const serviceChargeModel = getOtherServicesModel(invoiceOtherAmounts ?? {});
    const otherDocs =
      shipmentInfo?.shipmentInvoiceDetails?.invoiceDetails[0]?.files?.other;
    if (otherDocs) {
      const podFiles = Object.values(otherDocs).filter(
        (doc) => doc.documentId === "POD",
      );
      setPodDoc(podFiles);
    } else {
      setPodDoc(null);
    }
    setHoldPayout();
    setValue("invoiceDueDate", shipmentInfo?.creditInvoiceDueDate);

    setOtherServiceCharges(serviceChargeModel);

    setValue("shipmentStatus", shipmentInfo?.currentState?.state);

    if (isCreditOrder) {
      void getCreditPeriod();
    }
    /* eslint-enable no-alert */
  }, [shipmentInfo, isCreditOrder]);

  useEffect(() => {
    setValue(
      "invoiceNo",
      shipmentDetails?.shipmentInvoiceDetails?.invoiceDetails[0]
        ?.invoiceNumber ?? "",
    );
  }, [shipmentDetails]);

  const setHoldPayout = useCallback(() => {
    const { payoutHoldType, invoicePayoutHoldAmount, totalInvoicedAmount } =
      shipmentInfo?.shipmentInvoiceDetails; //NOSONAR

    setValue("holdType", payoutHoldType || release);

    payoutHoldType === fullHold &&
      setValue("payoutHoldAmount", totalInvoicedAmount);
    payoutHoldType === partialHold &&
      setValue("payoutHoldAmount", invoicePayoutHoldAmount);
    /* eslint-enable no-alert */
  }, [shipmentInfo]);

  const setDefaultValue = () => {
    reset();
    setHoldPayout();
  };

  const orderOnTrack = useMemo(() => {
    let dateIsAfter;

    if (shipmentInfo?.currentState?.state === SHIPMENT_STATUS.CANCELLED) {
      return SHIPMENT_STATUS_CHIP.CANCELLED;
    }

    const actualDispatchDate = moment
      .utc(shipmentDetails?.actualDispatchDate)
      .format("L");

    switch (shipmentInfo?.currentState?.state) {
      case SHIPMENT_STATUS.READY_FOR_DISPATCH:
        dateIsAfter = moment(
          shipmentDetails?.plannedDispatchDate,
        ).isSameOrAfter(moment());
        break;
      case SHIPMENT_STATUS.DISPATCHED:
      case SHIPMENT_STATUS.DELIVERED:
        dateIsAfter = moment(
          shipmentDetails?.plannedDispatchDate,
        ).isSameOrAfter(actualDispatchDate);
        break;
      default:
        dateIsAfter = true;
        break;
    }
    if (dateIsAfter) {
      return SHIPMENT_STATUS_CHIP.ON_TIME;
    } else {
      return SHIPMENT_STATUS_CHIP.DELAYED;
    }
  }, [
    shipmentDetails?.actualDispatchDate,
    shipmentDetails?.plannedDispatchDate,
    shipmentInfo?.currentState?.state,
  ]);

  const handleRadioButton = ({ target: { value } }) => {
    setValue("holdType", value);

    const { invoicePayoutHoldAmount, totalInvoicedAmount } =
      shipmentDetails?.shipmentInvoiceDetails; //NOSONAR

    if (value === fullHold) {
      setValue("payoutHoldAmount", totalInvoicedAmount);
    } else {
      value === partialHold && invoicePayoutHoldAmount
        ? setValue("payoutHoldAmount", invoicePayoutHoldAmount)
        : resetField("payoutHoldAmount");
    }
  };

  const updateInvoiceDueDate = useCallback(
    (date) => {
      setValue(
        "invoiceDueDate",
        moment(date)
          .add(creditPeriod ?? 0, "days")
          .format("YYYY-MM-DD"),
      );
    },
    [creditPeriod, setValue],
  );

  const { DISPATCHED, DELIVERED, INVOICED, COMPLETED, READY_TO_SHIP, PLANNED } =
    SHIPMENT_STATUS;

  const updateTrackingField = useCallback((key, value) => {
    setTrackingModalData((prev) => ({ ...prev, [key]: value }));
  }, []);

  const isModalFieldsDirty = useMemo(
    () =>
      trackingModalData.truckNumber !==
        (shipmentDetails?.vehicleDetails?.truckNumber ?? "") ||
      trackingModalData.driverNumber !==
        (shipmentDetails?.vehicleDetails?.driverNumber ?? "") ||
      trackingModalData.driverName !==
        (shipmentDetails?.vehicleDetails?.driverName ?? "") ||
      (trackingModalData.expectedReportingTime
        ? new Date(trackingModalData.expectedReportingTime).getTime()
        : null) !==
        (shipmentDetails?.eta ? new Date(shipmentDetails.eta).getTime() : null),
    [trackingModalData, shipmentDetails],
  );

  const isReasonValid = useMemo(
    () =>
      !!trackingModalData.trackingReason &&
      (trackingModalData.trackingReason !== OTHER_REASON_VALUE ||
        !!trackingModalData.trackingOtherReason?.trim()),
    [trackingModalData.trackingReason, trackingModalData.trackingOtherReason],
  );

  const isSaveTrackingEnabled = isModalFieldsDirty && isReasonValid;

  const handleSaveTrackingModal = useCallback(async () => {
    const finalReason =
      trackingModalData.trackingReason === OTHER_REASON_VALUE
        ? trackingModalData.trackingOtherReason?.trim()
        : trackingModalData.trackingReason;

    const tempValues = {
      ...getValues(),
      truckNumber: trackingModalData.truckNumber,
      driverNumber: trackingModalData.driverNumber,
      driverName: trackingModalData.driverName,
      expectedDeliveryDate: trackingModalData.expectedReportingTime
        ? new Date(trackingModalData.expectedReportingTime)
        : "",
      vehicleDetailsUpdateReason: finalReason,
    };

    const result = makeShipmentUpdationData(
      tempValues,
      shipmentDetails,
      shipmentList,
      isCreditOrder,
      isDirect,
    );
    setTrackingUpdateLoading(true);
    try {
      const data = await putCall_v2("/oms/shipment", result);

      if (data) {
        setValue("truckNumber", trackingModalData.truckNumber);
        setValue("driverNumber", trackingModalData.driverNumber);
        setValue("driverName", trackingModalData.driverName);
        setValue(
          "expectedDeliveryDate",
          trackingModalData.expectedReportingTime,
        );
        setValue("vehicleDetailsUpdateReason", finalReason);
        triggerToaster("Tracking details updated successfully", success);
        getShipmentData();
        setEditTrackingDetailsModalOpen(false);
      }
    } catch (err) {
      triggerToaster(
        err?.data?.detail ?? "Failed to update tracking details.",
        error,
      );
    } finally {
      setTrackingUpdateLoading(false);
    }
  }, [
    trackingModalData,
    shipmentDetails,
    shipmentList,
    isCreditOrder,
    isDirect,
    getValues,
    setValue,
    getShipmentData,
  ]);

  const handleOpenEditTracking = useCallback(() => {
    setTrackingModalData({
      truckNumber: shipmentDetails?.vehicleDetails?.truckNumber ?? "",
      driverNumber: shipmentDetails?.vehicleDetails?.driverNumber ?? "",
      driverName: shipmentDetails?.vehicleDetails?.driverName ?? "",
      expectedReportingTime: shipmentDetails?.eta
        ? new Date(shipmentDetails.eta)
        : null,
      trackingReason: "",
      trackingOtherReason: "",
    });
    setEditTrackingDetailsModalOpen(true);
  }, [shipmentDetails]);

  const handleNotifyVendor = useCallback(async () => {
    if (!shipmentDetails?.number) return;
    try {
      await postCall_v2(
        `/joms/api/retry-seller-indent/${shipmentDetails.number}`,
      );
      triggerToaster("Notification sent successfully", success);
    } catch {
      triggerToaster("Failed to send notification.", error);
    }
  }, [shipmentDetails]);

  if (!shipmentDetails) return <Loader />;

  const isPlantSupplyAndDispatched =
    isDirect &&
    [DISPATCHED, DELIVERED, INVOICED, COMPLETED].includes(
      shipmentDetails?.currentState?.state,
    );

  const isDispatchDateEnabled = [READY_TO_SHIP, DISPATCHED].includes(
    watch("shipmentStatus"),
  );

  const isInvoiceEnabled = [PLANNED, READY_TO_SHIP].includes(
    shipmentDetails?.currentState?.state,
  );

  const getMinDate = () => {
    if (orderDetails?.invoiceDate) {
      const invoiceMoment = getStartDate(orderDetails.invoiceDate);
      if (invoiceMoment?.isValid?.()) {
        return invoiceMoment.toDate();
      }
    }

    const orderMoment = getStartDate(orderDetails?.orderDate);
    if (orderMoment?.isValid?.()) {
      return orderMoment
        .clone()
        .subtract(orderDetails?.invoiceDateThreshold, "days")
        .toDate();
    }
  };

  const handleDeliveryModal = () => {
    setDeliveryModalOpen((prev) => !prev);
  };

  const OnDeliveryFileUpload = (file) => {
    setDeliveryProofFile(file);
  };

  // Validate delivery date against invoice date or order threshold
  const validateDeliveryDate = () => {
    const value = getValues("actualDeliveryDate");
    if (!value) {
      return { valid: false, message: "Delivery date is required" };
    }

    const deliveryMoment = getStartDate(value);
    if (!deliveryMoment?.isValid?.()) {
      return { valid: false, message: "Please enter a valid delivery date" };
    }

    const deliveryDateStr = deliveryMoment.toDate().toISOString().slice(0, 10);

    const resolvedInvoiceDate =
      getValues("invoiceDate") ??
      shipmentDetails?.shipmentInvoiceDetails?.invoiceDetails?.[0]
        ?.invoiceDate ??
      orderDetails?.invoiceDate;

    if (resolvedInvoiceDate) {
      const invoiceMoment = getStartDate(resolvedInvoiceDate);
      if (invoiceMoment?.isValid?.()) {
        const invoiceDateStr = invoiceMoment
          .toDate()
          .toISOString()
          .slice(0, 10);
        if (deliveryDateStr < invoiceDateStr) {
          return {
            valid: false,
            message: "Delivery date cannot be before the invoice date",
          };
        }
      }
    } else if (orderDetails?.orderDate) {
      const orderMoment = getStartDate(orderDetails.orderDate);
      if (orderMoment?.isValid?.()) {
        const thresholdDateStr = orderMoment
          .clone()
          .subtract(orderDetails?.invoiceDateThreshold, "days")
          .toDate()
          .toISOString()
          .slice(0, 10);
        if (deliveryDateStr < thresholdDateStr) {
          return {
            valid: false,
            message: `The date entered cannot be older than ${orderDetails?.invoiceDateThreshold} days from the Order Date`,
          };
        }
      }
    }

    return { valid: true };
  };

  const onSaveDelivery = async () => {
    const result = validateDeliveryDate();
    if (!result.valid) {
      setError("actualDeliveryDate", {
        type: "manual",
        message: result.message,
      });
      return;
    }

    clearErrors("actualDeliveryDate");

    await handleDeliveryUpdate();
  };

  const handleDeliveryUpdate = async () => {
    try {
      const formData = new FormData();
      formData.append("shipmentId", shipmentDetails?.id);
      formData.append("currentState", "dispatched");

      formData.append(
        "deliveryDate",
        new Date(getValues("actualDeliveryDate"))
          ?.toISOString()
          ?.replace("Z", ""),
      );
      formData.append("file", deliveryProofFile);

      await postCall_v2(
        "/joms/api/external/v1/shipment/marked-delivered",
        formData,
      );
      triggerToaster("Shipment marked as delivered successfully.", success);
      setDeliveryModalOpen(false);
      setDeliveryProofFile(null);
      getShipmentData();
    } catch (err) {
      triggerToaster(
        err?.data?.message ?? "Failed to mark shipment as delivered.",
        error,
      );
    }
  };

  const handlePodDownload = () => {
    if (podDoc?.length) {
      const podFile = podDoc[0];
      const fileObj = {
        generatedId: podFile.generatedId || podFile.id,
        originalName: podFile.originalName,
      };

      triggerDownloadInvoice(
        fileObj,
        "OTHERS",
        shipmentDetails?.id,
        getValues("invoiceNo") ||
          shipmentDetails?.shipmentInvoiceDetails?.invoiceDetails[0]
            ?.invoiceNumber,
      );
    }
  };

  const openUrlInNewTab = (url) => {
    if (!url) return;
    const formattedUrl =
      url.substring(0, 4) === "http" ? url : `https://${url}`;
    window.open(formattedUrl, "_blank");
  };

  const handleOpenPodLinks = async () => {
    try {
      const podDocuments = shipmentDetails?.podLinks?.podDocuments;
      if (!podDocuments?.length) return;

      const res = await postCall_v2(
        "joms/api/internal/v1/document/get-tms-signed-urls",
        [...podDocuments],
      );

      const urls = res?.data?.signedUrls || res?.data || [];
      downloadPdf(urls[0]);
    } catch {
      triggerToaster("Unable to load POD document", error);
    }
  };

  const isExpectedDeliveryDateEnabled = [
    PLANNED,
    READY_TO_SHIP,
    DISPATCHED,
  ].includes(watch("shipmentStatus"));

  const { vehicleDetails } = shipmentDetails;
  const { deliveryType } = orderDetails;
  return (
    <Box mb={4}>
      <CollapsibleInfo
        summaryStyles={{ alignItems: "center" }}
        defaultExpand={id === shipmentDetails?.id}
        summary={
          <ShipmentHeader
            invoicePostingStatus={getInvoicePostingStatus(shipmentDetails)}
            shipmentNumber={shipmentDetails?.number}
            plannedDispatchDate={shipmentDetails?.plannedDispatchDate}
            actualDispatchDate={shipmentDetails?.actualDispatchDate}
            sellerOrderNumber={shipmentDetails?.sellerOrderNumber}
            orderStatus={shipmentInfo?.currentState?.state}
            orderOnTrack={orderOnTrack}
            invoiceNumber={
              shipmentDetails?.shipmentInvoiceDetails?.invoiceDetails[0]
                ?.invoiceNumber ?? "-"
            }
            isCreditOrder={isCreditOrder}
            invoicePaymentStatus={shipmentDetails?.invoicePaymentStatus}
          />
        }
        details={
          <FormProvider {...methods}>
            <Box as="form" onSubmit={handleSubmit(onSubmitShipment)} p={2}>
              <ShipmentDeliveryData
                shipmentValue={
                  shipmentDetails?.shipmentInvoiceDetails?.shipmentValue
                }
                mode={shipmentDetails?.modeOfShipment}
                deliveryType={shipmentDetails?.deliveryType?.state}
              />
              <GridSkeleton
                containerStyle={{ marginBottom: 32 }}
                firstChild={
                  <Box>
                    {shipmentDetails?.currentState?.state ===
                    shipmentStatus.planned ? (
                      <>
                        <Typography
                          style={{ marginBottom: 8 }}
                          fontWeight={600}
                        >
                          Shipment status
                        </Typography>
                        <Controller
                          control={control}
                          name="shipmentStatus"
                          defaultValue={shipmentDetails.currentState?.state}
                          render={({ field: { value, onChange } }) => (
                            <DropdownV1
                              itemList={shipmentDetails.nextShipmentStates}
                              sx={{ width: "40%" }}
                              valueKey="state"
                              value={value}
                              onChange={onChange}
                            />
                          )}
                        />
                      </>
                    ) : (
                      <CustomViewOnly
                        label="Shipment status"
                        value={shipmentDetails.currentState?.state}
                      />
                    )}
                  </Box>
                }
                secondChild={
                  <Controller
                    control={control}
                    name="actualDispatchDate"
                    defaultValue={shipmentDetails?.actualDispatchDate ?? null}
                    render={({ field: { value, onChange } }) => (
                      <>
                        <Typography mb={2} fontWeight={600}>
                          Dispatch date
                        </Typography>
                        <DatePickerV1
                          rest={{ flexDirection: "column" }}
                          minDate={getMinDate()}
                          value={value}
                          onChange={onChange}
                          disabled={!isDispatchDateEnabled}
                          dateStyle={{ maxWidth: 180 }}
                        />
                        <ErrorMsg msg={errors?.actualDispatchDate?.message} />
                      </>
                    )}
                  />
                }
                thirdChild={
                  <Controller
                    control={control}
                    name="expectedDeliveryDate"
                    defaultValue={shipmentDetails?.eta ?? null}
                    render={({ field: { value, onChange } }) => (
                      <>
                        <Typography mb={2} fontWeight={600}>
                          Expected Delivery Date
                        </Typography>
                        <DatePickerV1
                          rest={{ flexDirection: "column" }}
                          value={value}
                          onChange={onChange}
                          disabled={!isExpectedDeliveryDateEnabled}
                          dateStyle={{ maxWidth: 180 }}
                        />
                        <ErrorMsg msg={errors.expectedDeliveryDate?.message} />
                      </>
                    )}
                  />
                }
              />
              <GridSkeleton
                containerStyle={{ marginBottom: 32 }}
                firstChild={
                  <Box>
                    <Box
                      display={"flex"}
                      flexDirection={"column"}
                      justifyContent={"center"}
                      alignItems={"flex-start"}
                    >
                      <Typography mb={2} fontWeight={600}>
                        Delivery date/Proof
                      </Typography>
                      <Box>
                        {shipmentDetails.currentState?.state ===
                        SHIPMENT_STATUS?.DISPATCHED ? (
                          <ButtonV1
                            title={"Add"}
                            onClick={handleDeliveryModal}
                            disabled={
                              shipmentDetails.currentState?.state !==
                              SHIPMENT_STATUS?.DISPATCHED
                            }
                          />
                        ) : (
                          <Box>
                            <Box display={"flex"} flexDirection={"row"} gap={2}>
                              <Typography>
                                {dateConvertor(
                                  shipmentDetails?.actualDeliveryDate,
                                )}
                              </Typography>
                              <Divider orientation="vertical" flexItem />
                              {!!podDoc?.length ? (
                                <Link
                                  onClick={handlePodDownload}
                                  color={theme.palette.success.dark}
                                  style={{ cursor: "pointer" }}
                                >
                                  {podDoc?.[0]?.originalName}
                                </Link>
                              ) : (
                                <>-</>
                              )}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>

                    <ModalBox
                      open={deliveryModalOpen}
                      onCloseModal={handleDeliveryModal}
                      hideCrossIcon
                    >
                      <Box width={"100%"}>
                        <Box
                          display={"flex"}
                          justifyContent={"space-between"}
                          mb={4}
                        >
                          <Typography fontSize={20} fontWeight={600}>
                            Add delivery date and proof
                          </Typography>
                          <Close
                            onClick={handleDeliveryModal}
                            style={{ cursor: "pointer" }}
                          />
                        </Box>
                        <Divider />
                        <Box
                          display={"flex"}
                          justifyContent={"center"}
                          mb={6}
                          mt={7}
                          width={"100%"}
                        >
                          <UploadPdf
                            allowedMaxSize={5}
                            fileCallBackFn={OnDeliveryFileUpload}
                          />
                        </Box>
                        <Divider />
                        <Box mb={6} mt={6}>
                          <Controller
                            control={control}
                            name="actualDeliveryDate"
                            defaultValue={
                              shipmentDetails?.actualDeliveryDate ?? null
                            }
                            render={({ field: { value, onChange } }) => (
                              <>
                                <Typography mb={2} fontWeight={600}>
                                  Delivery date
                                </Typography>
                                <DatePickerV1
                                  rest={{ flexDirection: "column" }}
                                  value={value}
                                  dateStyle={{ maxWidth: 180 }}
                                  onChange={onChange}
                                />
                                <ErrorMsg
                                  msg={errors.actualDeliveryDate?.message}
                                />
                              </>
                            )}
                          />
                        </Box>
                        <Divider />
                        <Box
                          display={"flex"}
                          alignItems={"center"}
                          justifyContent={"flex-end"}
                          gap={3}
                          mt={4}
                        >
                          <ButtonV1
                            variant="outlined"
                            title={"Close"}
                            onClick={handleDeliveryModal}
                          />
                          <ButtonV1
                            title={"Save"}
                            disabled={
                              !(
                                actualDeliveryDate ||
                                shipmentDetails?.actualDeliveryDate
                              )
                            }
                            onClick={onSaveDelivery}
                          />
                        </Box>
                      </Box>
                    </ModalBox>
                  </Box>
                }
                secondChild={
                  <CustomViewOnly
                    label="Ship from"
                    value={getShipFromAddress(lineItems[0]?.sourceSeller)}
                  />
                }
                thirdChild={
                  currentTcSync && (
                    <Box>
                      <Typography fontWeight={600} mb={1}>
                        Sync status
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Typography
                          sx={{
                            color: getTcSyncStatusColor(
                              currentTcSync?.syncStatus,
                              theme,
                            ),
                            fontWeight: 600,
                            textTransform: "capitalize",
                          }}
                        >
                          {currentTcSync?.syncStatus ?? "-"}
                        </Typography>

                        <TooltipLabel
                          placement="bottom"
                          title={
                            <Typography fontSize={12}>
                              {currentTcSync?.message}
                            </Typography>
                          }
                          labelChildren={
                            <InfoOutlinedIcon
                              sx={{
                                fontSize: 16,
                                color: theme.palette.text.secondary,
                                cursor: "pointer",
                              }}
                            />
                          }
                        />

                        {currentTcSync?.syncStatus === "failed" && (
                          <IconButton
                            size="small"
                            sx={{ ml: 0, p: 0 }}
                            onClick={() =>
                              handleRetryTcSync(currentTcSync?.invoiceNumber)
                            }
                          >
                            <ReplayCircleFilledIcon
                              sx={{
                                color: theme.palette.secondary.main,
                                fontSize: 20,
                              }}
                            />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  )
                }
              />
              <Box style={{ marginBottom: 32 }}>
                <ShipmentLineItem
                  retriggerCalculation={retriggerCalculation}
                  lineItemsDetails={shipmentDetails?.lineItemsDetails}
                  isPlantSupplyAndDispatched={isPlantSupplyAndDispatched}
                />
              </Box>
              <Divider style={{ marginBottom: 24 }} />
              <GridSkeleton
                containerStyle={{ marginBottom: 40 }}
                firstChild={
                  <Box style={{ display: "flex", flexDirection: "column" }}>
                    <Box style={{ display: "flex", alignItems: "flex-end" }}>
                      <Controller
                        control={control}
                        name={`invoiceNo`}
                        defaultValue={
                          shipmentDetails?.shipmentInvoiceDetails
                            ?.invoiceDetails[0]?.invoiceNumber ?? ""
                        }
                        render={({ field: { value, onChange, onBlur } }) => (
                          <Box
                            style={{ display: "flex", flexDirection: "column" }}
                          >
                            <Typography style={{ marginBottom: 8 }}>
                              Invoice No.
                            </Typography>
                            <CustomTextField
                              disablePaste={false}
                              value={value}
                              mask={true}
                              disabled={!isInvoiceEnabled}
                              onChange={onChange}
                              customPadding={2}
                              onBlur={onBlur}
                              highlightError={errors?.confirmInvoiceNo?.hasOwnProperty(
                                "message",
                              )}
                            />
                          </Box>
                        )}
                      />
                      <Controller
                        control={control}
                        name={`confirmInvoiceNo`}
                        defaultValue={
                          shipmentDetails?.shipmentInvoiceDetails
                            ?.invoiceDetails[0]?.invoiceNumber ?? ""
                        }
                        render={({ field: { value, onChange, onBlur } }) => (
                          <CustomTextField
                            disablePaste={false}
                            value={value}
                            onChange={onChange}
                            customPadding={2}
                            disabled={!isInvoiceEnabled}
                            onBlur={onBlur}
                            highlightError={errors?.confirmInvoiceNo?.hasOwnProperty(
                              "message",
                            )}
                          />
                        )}
                      />
                    </Box>
                    <ErrorMsg msg={errors?.confirmInvoiceNo?.message} />
                  </Box>
                }
                secondChild={
                  <Box style={{ display: "flex", flexDirection: "column" }}>
                    <Box
                      style={{
                        display: "flex",
                        alignItems: "flex-end",
                      }}
                    >
                      <Controller
                        control={control}
                        name="invoiceDate"
                        defaultValue={
                          shipmentDetails?.shipmentInvoiceDetails
                            ?.invoiceDetails[0]?.invoiceDate ?? null
                        }
                        render={({ field: { value, onChange } }) => (
                          <Box style={{ marginRight: 8 }}>
                            <Typography mb={2} fontWeight={600}>
                              Invoice date
                            </Typography>
                            <DatePickerV1
                              rest={{ flexDirection: "column" }}
                              disableFuture={true}
                              minDate={getMinDate()}
                              value={value}
                              customPadding={10}
                              dateStyle={{ maxWidth: 180 }}
                              onChange={onChange}
                            />
                          </Box>
                        )}
                      />
                      <Controller
                        control={control}
                        name="confirmInvoiceDate"
                        defaultValue={
                          shipmentDetails?.shipmentInvoiceDetails
                            ?.invoiceDetails[0]?.invoiceDate ?? null
                        }
                        render={({ field: { value, onChange } }) => {
                          const handleChange = (e) => {
                            updateInvoiceDueDate(e);
                            onChange(e);
                          };
                          return (
                            <DatePickerV1
                              rest={{ flexDirection: "column" }}
                              value={value}
                              disableFuture={true}
                              minDate={getMinDate()}
                              dateStyle={{ maxWidth: 180 }}
                              customPadding={10}
                              onChange={(e) => handleChange(e)}
                            />
                          );
                        }}
                      />
                    </Box>
                    <ErrorMsg msg={errors?.confirmInvoiceDate?.message} />
                  </Box>
                }
                thirdChild={
                  isCreditOrder && (
                    <CustomViewOnly
                      label="Invoice due date"
                      value={dateConvertor(invoiceDueDate)}
                    />
                  )
                }
              />
              <GridSkeleton
                containerStyle={{ marginBottom: 40 }}
                firstChild={
                  <Controller
                    control={control}
                    name={hookFormLabel.totalBaseInvoiceAmt}
                    defaultValue={shipmentDetails?.shipmentInvoiceDetails?.invoicedBaseAmount.toString()}
                    render={({ field: { value } }) => (
                      <CustomViewOnly
                        label="Invoiced amount (₹-base value)"
                        value={moneyString(value)}
                      />
                    )}
                  />
                }
                secondChild={
                  <Controller
                    control={control}
                    name={hookFormLabel.totalGSTInvoiceAmount}
                    defaultValue={shipmentDetails?.shipmentInvoiceDetails?.invoicedTaxAmount.toString()}
                    render={({ field: { value } }) => (
                      <CustomViewOnly
                        label="Invoiced amount (₹-only GST)"
                        value={moneyString(value)}
                      />
                    )}
                  />
                }
                thirdChild={
                  <Controller
                    control={control}
                    name="totalInvoiceAmt"
                    defaultValue={shipmentDetails?.shipmentInvoiceDetails?.totalInvoicedAmount.toString()}
                    render={({ field: { value } }) => (
                      <CustomViewOnly
                        label="Total invoiced amount(₹)"
                        value={
                          value === "0"
                            ? moneyFormat(value)
                            : moneyString(value)
                        }
                      />
                    )}
                  />
                }
              />
              <GridSkeleton
                firstChild={
                  <Box style={{ display: "flex", alignItems: "flex-end" }}>
                    <Controller
                      control={control}
                      name={hookFormLabel.totalInvoiceOtherServicesAmt}
                      defaultValue={shipmentDetails?.shipmentInvoiceDetails?.invoiceOtherServicesSum.toString()}
                      render={({ field: { value } }) => (
                        <CustomViewOnly
                          label="Invoiced amount (₹-other services)"
                          value={moneyString(value)}
                        />
                      )}
                    />
                    <ButtonV1
                      title="Add/View"
                      variant="text"
                      color="secondary"
                      disabled={shipmentDetails.currentState?.state !== PLANNED}
                      onClick={toggleOtherServicesModal}
                    />
                  </Box>
                }
                secondChild={
                  isDirect && (
                    <Box
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        width: "50%",
                      }}
                    >
                      <Typography style={{ marginBottom: 8 }}>
                        Seller Shipment no.
                      </Typography>
                      <Controller
                        control={control}
                        name={`sapShipmentNumber`}
                        defaultValue={shipmentDetails?.shipmentNumber}
                        render={({ field: { value, onChange } }) => (
                          <TextField
                            value={value}
                            onPaste={(e) => e.preventDefault()}
                            onChange={onChange}
                            customPadding={10}
                          />
                        )}
                      />
                    </Box>
                  )
                }
              />
              <GridSkeleton
                containerStyle={{ marginBottom: 40 }}
                firstChild={
                  <Box>
                    <Box display={"flex"} alignItems={"center"}>
                      <Typography
                        fontWeight={600}
                        marginRight={8}
                        whiteSpace="nowrap"
                      >
                        Hold payout:
                      </Typography>
                      <FormControl>
                        <RadioGroup
                          onChange={handleRadioButton}
                          value={holdType}
                          row
                        >
                          <FormControlLabel
                            value={fullHold}
                            control={<Radio size="small" />}
                            label="Full hold"
                            sx={{ fontSize: 14 }}
                          />
                          <FormControlLabel
                            value={partialHold}
                            control={<Radio size="small" />}
                            label="Partial hold"
                          />
                          <FormControlLabel
                            value={release}
                            control={<Radio size="small" />}
                            label="Release"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                    {holdType === fullHold && (
                      <HoldPayoutTextField
                        control={control}
                        holdType={holdType}
                      />
                    )}
                    {holdType === partialHold && (
                      <HoldPayoutTextField
                        control={control}
                        holdType={holdType}
                      />
                    )}
                    <ErrorMsg
                      msg={
                        holdType === partialHold
                          ? errors?.payoutHoldAmount?.message
                          : ""
                      }
                    />
                  </Box>
                }
                secondChild={
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box display={"flex"}>
                      <Typography style={{ marginBottom: 8 }} fontWeight={600}>
                        Invoice comment
                      </Typography>
                      {[fullHold, partialHold].includes(holdType) && (
                        <Typography style={{ color: theme.palette.error.dark }}>
                          *
                        </Typography>
                      )}
                    </Box>

                    <Controller
                      control={control}
                      name="invoiceComment"
                      defaultValue={
                        shipmentDetails?.shipmentInvoiceDetails
                          ?.invoiceComments ?? ""
                      }
                      render={({ field: { value, onChange } }) => (
                        <>
                          <TextareaAutosize
                            style={{
                              borderRadius: 8,
                              border: `1px solid ${theme.palette.grey[400]}`,
                              padding: 8,
                              fontFamily: "inherit",
                              resize: "none",
                            }}
                            value={value}
                            onChange={onChange}
                            minRows={2}
                            maxRows={2}
                          />
                          <ErrorMsg
                            msg={
                              holdType !== release
                                ? errors?.invoiceComment?.message
                                : ""
                            }
                          />
                        </>
                      )}
                    />
                  </Box>
                }
              />
              <Box
                style={{
                  borderBottom: 1,
                  borderColor: theme.palette.grey["400"],
                  borderBottomStyle: "solid",
                }}
                mb={12}
              >
                <Box display={"flex"} justifyContent={"space-between"} mb={4}>
                  <Box display={"flex"} alignItems={"center"} gap={4}>
                    <Typography fontWeight={"bold"}>
                      Tracking details
                    </Typography>
                    <ButtonV1
                      startIcon={
                        <Edit
                          fontSize="16"
                          sx={{
                            color: theme.palette.text.disabled,
                          }}
                        />
                      }
                      title="Edit"
                      onClick={handleOpenEditTracking}
                      color="secondary"
                      disabled={
                        shipmentInfo?.currentState?.state !== PLANNED ||
                        shipmentDetails?.deliveryType?.state !== "JOTS"
                      }
                    />
                    <ButtonV1
                      startIcon={<NotificationsNone fontSize="16" />}
                      title="Notify"
                      onClick={handleNotifyVendor}
                      variant="outlined"
                      disabled={
                        shipmentInfo?.currentState?.state !== PLANNED ||
                        shipmentDetails?.deliveryType?.state !== "JOTS"
                      }
                    />
                  </Box>
                  <Box display={"flex"} alignItems={"center"} mr={12}>
                    <ButtonV1
                      startIcon={
                        <Place
                          fontSize="16"
                          sx={{
                            color: shipmentDetails?.trackShipmentUrl
                              ? theme.palette.primary.main
                              : theme.palette.text.disabled,
                          }}
                        />
                      }
                      disableRipple={true}
                      variant="text"
                      disabled={!shipmentDetails?.trackShipmentUrl}
                      title="Track shipment"
                      size="small"
                      onClick={() =>
                        openUrlInNewTab(shipmentDetails?.trackShipmentUrl)
                      }
                    />
                  </Box>
                </Box>
                <Box mb={8}>
                  <GridSkeleton
                    containerStyle={{ marginBottom: 40 }}
                    firstChild={
                      <>
                        <Typography style={{ marginBottom: 8 }}>
                          Driver No.
                        </Typography>
                        <Controller
                          control={control}
                          name={`driverNumber`}
                          defaultValue={
                            shipmentDetails?.vehicleDetails?.driverNumber
                          }
                          render={({ field: { value, onChange } }) => (
                            <TextField
                              value={value}
                              size="small"
                              type="number"
                              onChange={onChange}
                              disabled={shouldDisableTrackingField(
                                deliveryType,
                                vehicleDetails?.driverNumber,
                              )}
                            />
                          )}
                        />
                        <ErrorMsg msg={errors?.driverNumber?.message} />
                      </>
                    }
                    secondChild={
                      <>
                        <Typography style={{ marginBottom: 8 }}>
                          Truck No.
                        </Typography>
                        <Controller
                          control={control}
                          name={`truckNumber`}
                          defaultValue={
                            shipmentDetails?.vehicleDetails?.truckNumber
                          }
                          render={({ field: { value, onChange } }) => (
                            <TextField
                              value={value}
                              size="small"
                              onChange={onChange}
                              disabled={shouldDisableTrackingField(
                                deliveryType,
                                vehicleDetails?.truckNumber,
                              )}
                            />
                          )}
                        />
                        <ErrorMsg msg={errors?.truckNumber?.message} />
                      </>
                    }
                    thirdChild={
                      <>
                        <Typography style={{ marginBottom: 8 }}>
                          LR/RR No.
                        </Typography>
                        <Controller
                          control={control}
                          name={`lorryReceiptNumber`}
                          defaultValue={
                            shipmentDetails?.vehicleDetails?.lorryReceiptNumber
                          }
                          render={({ field: { value, onChange } }) => (
                            <TextField
                              value={value}
                              size="small"
                              onChange={onChange}
                              disabled={shouldDisableTrackingField(
                                deliveryType,
                                vehicleDetails?.lorryReceiptNumber,
                              )}
                            />
                          )}
                        />
                        <ErrorMsg msg={errors?.lorryReceiptNumber?.message} />
                      </>
                    }
                  />
                  <GridSkeleton
                    containerStyle={{ marginBottom: 40 }}
                    firstChild={
                      <>
                        <CustomViewOnly
                          label={"Logistics status"}
                          value={shipmentDetails?.logisticsStatus ?? "-"}
                        />
                      </>
                    }
                    secondChild={
                      shipmentDetails?.podLinks && (
                        <>
                          <CustomViewOnly
                            label={"POD links"}
                            value={
                              <ButtonV1
                                style={{ padding: 0 }}
                                startIcon={<Link />}
                                variant="text"
                                title="View POD links"
                                onClick={handleOpenPodLinks}
                              />
                            }
                          />
                        </>
                      )
                    }
                  />
                </Box>
              </Box>
              <EditTrackingDetailsModal
                open={editTrackingDetailsModalOpen}
                onClose={() => setEditTrackingDetailsModalOpen(false)}
                shipmentNumber={shipmentDetails?.number}
                trackingModalData={trackingModalData}
                updateTrackingField={updateTrackingField}
                isSaveTrackingEnabled={isSaveTrackingEnabled}
                trackingUpdateLoading={trackingUpdateLoading}
                onSave={handleSaveTrackingModal}
              />
              <ModalBox
                open={isOtherServicesModalOpen}
                onCloseModal={toggleOtherServicesModal}
                avoidBackDropClick={true}
                width={"45%"}
                paddingTop={0}
                hideCrossIcon={true}
                handleCloseIconClick={resetUnconfirmedCharges}
              >
                {/* {isZohoCustomer ? ( 
                  
                ) : (
                  <OtherServiceCharges
                    ref={otherChargeRef}
                    sellerOrderId={shipmentDetails?.sellerOrderId}
                    serviceCharges={getOtherServicesModel(
                      shipmentDetails?.shipmentInvoiceDetails
                        ?.invoiceOtherAmounts,
                    )}
                    submitCharges={() => {
                      calculateInvoiceAmt();
                      toggleOtherServicesModal();
                    }}
                    shipmentId={shipmentDetails?.id}
                    onCloseModal={toggleOtherServicesModal}
                  />
                )} */}
                <ZohoOtherServiceCharges
                  ref={otherChargeRef}
                  sellerOrderId={shipmentDetails?.sellerOrderId}
                  serviceCharges={getZohoOtherServicesModel(
                    zohoProcessingCharges,
                  )}
                  submitCharges={() => {
                    calculateInvoiceAmt();
                    toggleOtherServicesModal();
                  }}
                  shipmentId={shipmentDetails?.id}
                  onCloseModal={toggleOtherServicesModal}
                  processingCharges={shipmentDetails?.processing_charges}
                />
              </ModalBox>
              {/* File upload section */}
              <FileUpload
                fileList={
                  shipmentDetails?.shipmentInvoiceDetails?.invoiceDetails[0]
                    ?.files
                }
                shipmentId={shipmentDetails?.id}
                invoiceNumber={watch("invoiceNo")}
                shipmentNumber={shipmentDetails?.number}
                ref={fileUploadRef}
              />
              <FloatingBar bottom={10} position="sticky">
                <Box display={"flex"} gap={6}>
                  <ButtonV1
                    // disabled={
                    //   orderDetails?.orderStatus === orderStatus?.delivered
                    // }
                    title="Update"
                    type="submit"
                    loader={loading}
                  />
                  <ButtonV1
                    variant="outlined"
                    title="Reset"
                    onClick={setDefaultValue}
                    disabled={!isDirty}
                  />
                </Box>
              </FloatingBar>
              {/* <DialogBox
                handleClose={() => setIsConfirmModalOpen(false)}
                open={isConfirmModalOpen}
                handleConfirm={handleConfirm}
                message={SHORT_CLOSE_MSG}
                title=""
                msgStyle={{
                  color: (theme) => theme.palette.grey["A800"],
                  fontWeight: "600",
                  fontSize: 16,
                }}
                buttonWrapperStyle={{ justifyContent: "center" }}
                okButtonVariant="contained"
                cancelButtonVariant="outlined"
                isCloseIcon={true}
              />

              <PayoutCreationModal
                isShortCloseLoader={isShortCloseLoader}
                isShortClosePayout={true}
                open={isPayoutModal}
                onCloseModal={() => setIsPayoutModal(false)}
                onConfirm={handlePayoutConfirmation}
              /> */}
            </Box>
          </FormProvider>
        }
      />
    </Box>
  );
};

ShipmentUpdate.propTypes = {
  shipmentInfo: PropTypes.object,
  id: PropTypes.number,
  getShipmentData: PropTypes.func,
  zohoProcessingCharges: PropTypes.array,
};

export default ShipmentUpdate;
